import { useQuery, useInfiniteQuery, UseQueryOptions } from '@tanstack/react-query';
import { GetStagesItem } from '@microtica/ms-engine-sdk';
import { getPipelinesService, getEnvironmentService } from '../../backend';
import { PipelineItem } from '../../types';

// Query keys
export const pipelineKeys = {
  all: ['pipelines'] as const,
  lists: () => [...pipelineKeys.all, 'list'] as const,
  // Use this for invalidating all pipelines for a project (matches any filters)
  listByProject: (projectId: string) => [...pipelineKeys.lists(), projectId] as const,
  // Use this for the actual query key (includes filters)
  list: (projectId: string, filters?: { envIds?: string[]; repoNames?: string[] }) => 
    [...pipelineKeys.listByProject(projectId), filters] as const,
  environments: (projectId: string) => [...pipelineKeys.all, 'environments', projectId] as const,
};

export interface PipelineFilters {
  envIds?: string[];
  repoNames?: string[];
}

export interface PipelinesResponse {
  pipelines: PipelineItem[];
  total: number;
}

/**
 * Hook to fetch environments for pipeline filters dropdown
 */
export function useEnvironmentsForPipelines(
  projectId: string,
  options?: Omit<UseQueryOptions<GetStagesItem[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<GetStagesItem[], Error>({
    queryKey: pipelineKeys.environments(projectId),
    queryFn: async () => {
      const { data: { stages } } = await getEnvironmentService().getStages(projectId);
      return stages;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch pipelines with infinite scroll support
 */
export function usePipelines(
  projectId: string,
  filters: PipelineFilters = {},
  pageSize: number = 15
) {
  return useInfiniteQuery({
    queryKey: pipelineKeys.list(projectId, filters),
    queryFn: async ({ pageParam = 0 }) => {
      const { data: { pipelines, total } } = await getPipelinesService().getCombinedPipelines(
        projectId,
        {
          envIds: filters.envIds && filters.envIds.length > 0 ? filters.envIds : undefined,
          repoNames: filters.repoNames && filters.repoNames.length > 0 ? filters.repoNames : undefined,
          limit: pageSize,
          offset: pageParam as number,
        }
      );

      // Transform dates
      const mappedPipelines: PipelineItem[] = pipelines.map((p: any) => ({
        ...p,
        startDate: new Date(p.startDate),
        stopDate: p.stopDate ? new Date(p.stopDate) : undefined,
      }));

      return {
        pipelines: mappedPipelines,
        total,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, page) => sum + page.pipelines.length, 0);
      if (totalLoaded < lastPage.total && lastPage.pipelines.length === pageSize) {
        return totalLoaded;
      }
      return undefined;
    },
    initialPageParam: 0,
    staleTime: 15 * 1000, // 15 seconds - pipelines are more dynamic
    gcTime: 3 * 60 * 1000, // 3 minutes
  });
}
